import React from 'react';
import * as PropTypes from 'prop-types';
import { Table, Button, Popover, OverlayTrigger } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrophyAlt } from '@whitebox-learning/fontawesome-pro-light-svg-icons';
import { PAGE_JOURNAL, PAGE_OUTPUTS } from '../../constants/pages';
import { GameModeIcon } from '../Common/Icons';

export default function SimulationResults({ setMainPage, reset, results }) {
  if (!results) {
    // our results are missing for some reason, just show the reset screen
    // this should really only happen in dev when we are faking the competition
    return (
      <div style={{ padding: '5px' }}>
        <p className="lead">
          Results not available, please setup a new competition
        </p>
        <Button onClick={reset}>Reset Simulation</Button>
      </div>
    );
  }

  const { design1: d1, design2: d2, winner } = results;
  // make a list of all stat rows from both designs by combining and then generating a unique combination
  const d1Stats = d1.stats || [];
  const d2Stats = d2.stats || [];

  const fields = [
    ...d1Stats.map((row) => row.name),
    ...d2Stats.map((row) => row.name),
  ].reduce((carry, curr) => {
    if (curr === 'Name') {
      return carry; // don't need name here because we get it from d1 and d2 directly
    }
    if (carry.indexOf(curr) < 0) {
      carry.push(curr);
    }
    return carry;
  }, []);

  const stats = fields.map((field) => {
    const d1Entry = d1Stats.find((stat) => stat.name === field);
    const d2Entry = d2Stats.find((stat) => stat.name === field);
    return (
      <ResultRow
        key={field}
        item={field}
        design1={d1Entry && d1Entry.value}
        design2={d2Entry && d2Entry.value}
        winner={winner}
      />
    );
  });

  const whatsNext = (
    <Popover id="competition-results-help" title="What's Next?">
      <ul style={{ paddingLeft: '10px' }}>
        <li>
          Consider the results. Did your design perform as expected? What can
          you do to improve the design? It is a good practice to capture these
          thoughts in the{' '}
          <Button size="xs" onClick={() => setMainPage(PAGE_JOURNAL)}>
            Journal
          </Button>
          . Go to the Engineering Logs page in the Journal to amend or edit your
          notes about this design revision.
        </li>
        <li>
          If your design is "OUT OF SPEC", you should go to the{' '}
          <Button size="xs" onClick={() => setMainPage(PAGE_OUTPUTS)}>
            Outputs
          </Button>{' '}
          page and select the Design Specifications report to see which design
          features need your attention.
        </li>
        <li>
          Return to the research, design, or analysis tools to create a new
          design revision based on this new information.
        </li>
      </ul>
    </Popover>
  );

  return (
    <div className="simulation-results d-flex flex-column">
      <h1 className="title border-bottom">Competition</h1>

      <div className="rounded bg-white p-3 m-3 overflow-auto justify-content-center">
        <h4 className="text-center">Results</h4>

        <Table>
          <tbody>
            <ResultRow
              key="name"
              item="name"
              design1={d1.name}
              design2={d2.name}
              winner={winner}
            />
            {stats}
          </tbody>
        </Table>
        <WinnerBlock winner={winner} design1={d1} design2={d2} />
        <div className="d-flex justify-content-between border-top border-bottom py-3">
          <OverlayTrigger
            trigger="click"
            rootClose
            placement="right"
            overlay={
              <Popover id="competition-results-help">
                <Popover.Header>What's Next?</Popover.Header>
                <Popover.Body>
                  <ul className="ps-3 fs-6">
                    <li>
                      Consider the results. Did your design perform as expected?
                      What can you do to improve the design? It is a good
                      practice to capture these thoughts in the{' '}
                      <strong>Journal</strong>. Go to the Engineering Logs page
                      in the Journal to amend or edit your notes about this
                      design revision.
                    </li>
                    <li>
                      If your design is "OUT OF SPEC", you should go to the{' '}
                      <strong>Outputs</strong> page and select the Design
                      Specifications report to see which design features need
                      your attention.
                    </li>
                    <li>
                      Return to the research, design, or analysis tools to
                      create a new design revision based on this new
                      information.
                    </li>
                  </ul>
                </Popover.Body>
              </Popover>
            }
          >
            <Button variant="wb">What's Next?</Button>
          </OverlayTrigger>
          <Button onClick={reset}>Reset Simulation</Button>
        </div>
      </div>
    </div>
  );
}
SimulationResults.propTypes = {
  winner: PropTypes.number,
};
SimulationResults.defaultProps = {
  winner: 0,
  design1: {},
  design2: {},
};

function ResultRow({ item, winner, design1, design2 }) {
  return (
    <tr>
      <td>
        <b>{item}</b>
      </td>
      <td className={winner === 1 ? 'winner' : ''}>{design1}</td>
      <td className={winner === 2 ? 'winner' : ''}>{design2}</td>
    </tr>
  );
}

ResultRow.defaultProps = {
  item: '',
  winner: 0,
  design1: '',
  design2: '',
};

function WinnerBlock({ winner, design1, design2 }) {
  let winnerText = '';
  if (winner === 0 || winner === undefined) {
    return null;
  } else if (winner === 1) {
    winnerText = design1.name;
  } else if (winner === 2) {
    winnerText = design2.name;
  } else if (winner === 3) {
    winnerText = "It's a tie!";
  }

  return (
    <div className={`simulation-winner winner-${winner}`}>
      <GameModeIcon />
      <div className="winner-text">
        <span className="winner-label">The winner is: </span>
        <h2 className="winner-name">{winnerText}</h2>
      </div>
    </div>
  );
}
